import { ref, computed } from '@vue/composition-api'
import axios from '@axios'
import store from '@/store'

export const CLASSIFICATION_OPTIONS = [
  { value: 'siniestro', label: 'Siniestro' },
  { value: 'incidente', label: 'Incidente' },
]

const categoryRoots = ref([])
const loadingCategories = ref(false)
const allPriorities = ref([])
const cameraOptions = ref([])
const loadingCameras = ref(false)
const providerOptions = ref([])

// Solo estas prioridades se usan en mesa de ayuda, aunque en la BD haya otras activas
const ALLOWED_PRIORITY_CODES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']

const priorityOptions = computed(() => allPriorities.value
  .filter(p => p.active && ALLOWED_PRIORITY_CODES.includes(p.code))
  .sort((a, b) => a.level - b.level)
  .map(p => ({ value: p.id, label: p.name })))

const priorityOptionsWith = currentId => {
  // Un ticket anterior puede tener una prioridad que ya no se ofrece (Muy baja, Muy alta)
  const listed = priorityOptions.value.some(o => o.value === currentId)
  const current = !listed && allPriorities.value.find(p => p.id === currentId)
  return current
    ? [...priorityOptions.value, { value: current.id, label: `${current.name} (inactiva)` }]
    : priorityOptions.value
}

const originOptions = computed(() => categoryRoots.value.map(root => ({
  value: root.id,
  label: root.name,
  code: root.code,
})))

const typeOptionsFor = originId => categoryRoots.value
  .filter(root => !originId || root.id === originId)
  .flatMap(root => (root.children_recursive || []).map(type => ({
    value: type.id,
    label: type.name,
    code: type.code,
    children: type.children_recursive || [],
  })))

const typeOptions = computed(() => typeOptionsFor(null))

const subtypeOptionsFor = typeId => {
  const type = typeOptions.value.find(t => t.value === typeId)
  return type ? type.children.map(sub => ({ value: sub.id, label: sub.name })) : []
}

const unitOptions = computed(() => store.getters['unit/getSelectedItems'])

const loadCategories = async catalog => {
  categoryRoots.value = []
  if (!catalog) return
  loadingCategories.value = true
  try {
    categoryRoots.value = await store.dispatch('helpdesk/getTicketFailureCategories', catalog)
  } finally {
    loadingCategories.value = false
  }
}

const loadPriorities = async () => {
  allPriorities.value = await store.dispatch('helpdesk/getPriorities')
}

const loadUnits = () => store.dispatch('unit/getAll', {})

const loadCameras = async unitId => {
  cameraOptions.value = []
  if (!unitId) return
  loadingCameras.value = true
  try {
    const { data } = await axios.get('v1/proxy_camera', { params: { units: [unitId], max: 1000 } })
    cameraOptions.value = (data.success ? data.data : []).map(c => ({
      value: c.id,
      label: (c.camera && c.camera.name) || `Cámara ${c.id}`,
    }))
  } finally {
    loadingCameras.value = false
  }
}

const loadProviders = async () => {
  const providers = await store.dispatch('helpdesk/getProviders')
  providerOptions.value = providers.map(p => ({
    value: p.id,
    label: p.name ? `${p.name} (${p.email})` : p.email,
  }))
}

const splitCategory = category => {
  if (!category) return { originId: null, typeId: null, subtypeId: null }
  if (category.level >= 3) {
    return {
      originId: category.parent ? category.parent.parent_id : null,
      typeId: category.parent_id,
      subtypeId: category.id,
    }
  }
  return { originId: category.parent_id, typeId: category.id, subtypeId: null }
}

const ensurePriorities = async () => {
  if (!allPriorities.value.length) await loadPriorities()
}

export default function useTicketCatalogs() {
  return {
    categoryRoots,
    loadingCategories,
    originOptions,
    typeOptions,
    typeOptionsFor,
    subtypeOptionsFor,
    priorityOptions,
    priorityOptionsWith,
    unitOptions,
    cameraOptions,
    loadingCameras,
    providerOptions,
    loadCategories,
    loadPriorities,
    ensurePriorities,
    loadUnits,
    loadCameras,
    loadProviders,
    splitCategory,
  }
}